import React, { useState } from 'react';

import { DateTime } from 'luxon';

import { Alert, Breadcrumb, Panel } from '@shared/components/bootstrap';

import { AvailabilityControls } from '@admin/components/availability/controls';
import { PageHeader } from '@admin/components/helpers/page_header';
import { Pagination } from '@admin/components/pagination';
import { Spinner } from '@admin/components/spinner';
import { warehouseCapacityNewURL, warehouseOperatingScheduleTemplateNewURL } from '@admin/config/routes';
import { usePermission } from '@admin/hooks/use_permission';
import { useTitle } from '@admin/hooks/use_title';
import { AVAILABILITY_ADMIN } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import {
  Availability__BlockReasonEnum,
  Availability__FacilityCuftLimitFiltersInput,
  AvailabilityFacilityCuftLimitsDocument,
  useAvailabilityFacilityCuftLimitsDestroyMutation,
  useAvailabilityFacilityCuftLimitsQuery,
} from '@admin/schema';

import { Entries } from './entries';
import { Filters } from './filters';

const DEFAULT_FILTERS = {
  warehouseIDs: [],
  fromDate: DateTime.local().toISODate(),
  tillDate: DateTime.local().plus({ days: 30 }).toISODate(),
};

export const List: React.FC = () => {
  useTitle('Warehouse Capacity');
  const [filters, setFilters] = useState<Availability__FacilityCuftLimitFiltersInput>(DEFAULT_FILTERS);
  const [page, setPage] = useState<number>();
  const [selectedIDs, setSelectedIDs] = useState<string[]>([]);

  const { data, loading: querying } = useAvailabilityFacilityCuftLimitsQuery({
    client,
    variables: {
      page,
      filters: {
        warehouseIDs: filters.warehouseIDs,
        fromDate: filters.fromDate || null,
        tillDate: filters.tillDate || null,
      },
    },
  });

  const [destroy, { data: destroyData, loading: destroying }] = useAvailabilityFacilityCuftLimitsDestroyMutation({
    client,
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: AvailabilityFacilityCuftLimitsDocument,
        variables: {
          page,
          filters: {
            warehouseIDs: filters.warehouseIDs,
            fromDate: filters.fromDate || null,
            tillDate: filters.tillDate || null,
          },
        },
      },
    ],
    onCompleted: () => setSelectedIDs([]),
  });

  const onDestroy = async () => {
    await destroy({
      variables: {
        IDs: selectedIDs,
      },
    });
  };

  const onPageChange = (pageNumber: number) => {
    setSelectedIDs([]);
    setPage(pageNumber);
  };

  const canModify = usePermission(AVAILABILITY_ADMIN.resource, AVAILABILITY_ADMIN.actions.update);
  const paginated = data?.paginated;
  const options =
    paginated?.results
      .filter((result) => result.reason !== Availability__BlockReasonEnum.WeeklySchedule)
      .map(({ id }) => id) ?? [];
  const loading = querying || destroying;

  return (
    <>
      <PageHeader>
        <Breadcrumb>
          <Breadcrumb.Item active>Warehouse Capacity</Breadcrumb.Item>
        </Breadcrumb>
      </PageHeader>
      <Panel>
        <Panel.Body>
          {destroyData?.result?.error && <Alert style="danger">{destroyData.result.error}</Alert>}
          <Filters filters={filters} onChange={setFilters} />
          <AvailabilityControls
            createHref={warehouseCapacityNewURL()}
            weeklyScheduleHref={warehouseOperatingScheduleTemplateNewURL()}
            loading={loading}
            options={options}
            selectedOptions={selectedIDs}
            create={canModify}
            destroy={canModify}
            onDestroy={onDestroy}
            setSelectedOptions={setSelectedIDs}
          />
          {querying && <Spinner />}
          {paginated && (
            <Entries
              facilityCuftLimits={paginated.results}
              selectedOptions={selectedIDs}
              setSelectedOptions={setSelectedIDs}
            />
          )}
          {paginated && <Pagination onPage={onPageChange} pagination={paginated.pagination} />}
        </Panel.Body>
      </Panel>
    </>
  );
};
